import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Crown } from "lucide-react";

type Circle = {
  id: string; name: string; description: string | null; circle_type: string;
  min_support_cents: number; min_loyalty_score: number; perks: string[]; creator_id: string;
};

export default function CreatorCircles() {
  const { user } = useAuth();
  const [circles, setCircles] = useState<Circle[]>([]);
  const [member, setMember] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", circle_type: "vip",
    min_support_cents: "0", min_loyalty_score: "0", perks: "",
  });

  const load = async () => {
    const { data } = await supabase.from("creator_circles").select("*").order("created_at", { ascending: false });
    setCircles((data as any[] as Circle[]) || []);
    if (user) {
      const { data: m } = await supabase.from("circle_members").select("circle_id").eq("fan_id", user.id);
      setMember(new Set((m as any[] | null)?.map((x) => x.circle_id) || []));
    }
  };
  useEffect(() => { load(); }, [user]);

  const submit = async () => {
    if (!user || !form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    const perks = form.perks.split("\n").map((p) => p.trim()).filter(Boolean);
    const { error } = await supabase.from("creator_circles").insert({
      creator_id: user.id,
      name: form.name.trim(),
      description: form.description.trim() || null,
      circle_type: form.circle_type,
      min_support_cents: Math.round(parseFloat(form.min_support_cents || "0") * 100),
      min_loyalty_score: parseInt(form.min_loyalty_score || "0") || 0,
      perks,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Circle created");
    setOpen(false);
    setForm({ name: "", description: "", circle_type: "vip", min_support_cents: "0", min_loyalty_score: "0", perks: "" });
    load();
  };

  const join = async (circleId: string) => {
    if (!user) return;
    const { error } = await supabase.from("circle_members").insert({ circle_id: circleId, fan_id: user.id });
    if (error) { toast.error(error.message); return; }
    toast.success("Welcome to the circle");
    load();
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold flex-1">Creator Circles</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus className="size-4 mr-1" />New</Button></DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create a circle</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={400} /></div>
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.circle_type} onValueChange={(v) => setForm({ ...form, circle_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vip">VIP</SelectItem>
                      <SelectItem value="founders">Founders</SelectItem>
                      <SelectItem value="insiders">Insiders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Min support ($)</Label><Input type="number" value={form.min_support_cents} onChange={(e) => setForm({ ...form, min_support_cents: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Min loyalty</Label><Input type="number" value={form.min_loyalty_score} onChange={(e) => setForm({ ...form, min_loyalty_score: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Perks (one per line)</Label><Textarea rows={3} value={form.perks} onChange={(e) => setForm({ ...form, perks: e.target.value })} /></div>
                <Button onClick={submit} disabled={busy} className="w-full">{busy ? "Saving…" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {circles.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground"><Crown className="size-10 mx-auto mb-2" />No circles yet.</CardContent></Card>
        ) : circles.map((c) => {
          const mine = c.creator_id === user?.id;
          const isMember = member.has(c.id);
          return (
            <Card key={c.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2"><Crown className="size-4 text-amber-500" />{c.name}</CardTitle>
                  <Badge variant="outline" className="capitalize">{c.circle_type}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                {Array.isArray(c.perks) && c.perks.length > 0 && (
                  <ul className="text-xs space-y-0.5">{c.perks.map((p, i) => <li key={i}>✨ {p}</li>)}</ul>
                )}
                <div className="flex items-center justify-between text-xs pt-2 border-t">
                  <span className="text-muted-foreground">
                    Min: ${(c.min_support_cents / 100).toFixed(0)} support · {c.min_loyalty_score} loyalty
                  </span>
                  {mine ? <Badge>Yours</Badge> : isMember ? <Badge variant="secondary">Member</Badge> : <Button size="sm" onClick={() => join(c.id)}>Join</Button>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
